/**
 * ReportViewer — Visor DevExpress XtraReports con el componente React oficial
 * (devexpress-reporting-react). Reporte identificado como external:{IdExterno}.
 */
import React, {
  useEffect, useState, useRef, useImperativeHandle, forwardRef,
} from 'react';
import PropTypes from 'prop-types';
import { Alert, Spin } from 'antd';
import { getReportesConfig } from 'api/configuracion/reportesConfig';
import 'devextreme/dist/css/dx.light.css';
import './viewer-overrides.css';
// eslint-disable-next-line import/no-unresolved
import { locale, loadMessages } from 'devextreme/localization';
// eslint-disable-next-line import/no-unresolved
import esMessages from 'devextreme/localization/messages/es.json';

/* eslint-disable max-len */
import '@devexpress/analytics-core/dist/css/dx-analytics.common.css';
import '@devexpress/analytics-core/dist/css/dx-analytics.material.blue.light.css';
import 'devexpress-reporting/dist/css/dx-webdocumentviewer.css';
import { loadMessages as loadReportMsgs } from '@devexpress/analytics-core/analytics-localization';
import { DxReportViewer, RequestOptions, Callbacks } from 'devexpress-reporting-react/dx-report-viewer';
import esReporting from './dx-reporting.es.json';
/* eslint-enable max-len */

// Localización al español: widgets DevExtreme + textos del visor (mismo archivo de
// strings que usa el reporteador .NET). Se aplica al cargar el módulo, antes del visor.
loadMessages(esMessages);
loadReportMsgs(esReporting);
locale('es');

const {
  REACT_APP_REPORTEADOR_SCHEME: SCHEME,
  REACT_APP_REPORTEADOR_APIHOST: APIHOST,
} = process.env;

const REPORTEADOR_HOST = `${SCHEME}://${APIHOST}/`;

// Ruta del WebDocumentViewerController en el reporteador (.NET, {controller}/Invoke).
const INVOKE_ACTION = 'CustomWebDocumentViewer/Invoke';

/**
 * @param {object}  props
 * @param {string}  [props.pageKey]  - Clave de página (busca report_id en Django).
 * @param {number}  [props.reportId] - ID directo (omite la consulta a Django).
 * @param {string}  [props.height]   - Alto del visor CSS (default: '80vh').
 * @param {object}  [props.parameters] - Valores fijos a precargar en los parámetros del
 *   reporte (clave = Name del Parameter en el .repx, valor = valor a asignar). Se aplican
 *   en el evento ParametersInitialized, antes de que el visor pida los parámetros al usuario.
 * @param {boolean} [props.hideParametersPanel] - Si es true, nunca muestra el panel de
 *   parámetros: usa los `parameters` ya precargados (u otros defaults del reporte) y envía
 *   la solicitud de inmediato. Pensado para pantallas donde el parámetro ya viene fijo
 *   (p.ej. el ID de una Nómina ya seleccionada) y no debe ser editable por el usuario.
 * @param {object}  ref - Expone `getParameterValue(name)` para leer el valor actual
 *   de un parámetro del reporte (busca por `path`, que corresponde al `Name` del parámetro).
 */
const ReportViewer = forwardRef(({
  pageKey, reportId: reportIdProp, height, parameters, hideParametersPanel,
}, ref) => {
  const [resolvedId, setResolvedId] = useState(reportIdProp ?? null);
  const [loading, setLoading] = useState(!reportIdProp);
  const [error, setError] = useState(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const viewerRef = useRef(null);
  const containerRef = useRef(null);
  const hasRenderedOnceRef = useRef(false);
  const retryCountRef = useRef(0);
  const retryTimeoutIdRef = useRef(null);
  // El envio automatico (ver mas abajo) ya surtio efecto: deja de reintentar el clic.
  const autoSubmitDoneRef = useRef(false);
  // Con hideParametersPanel el visor se tapa con un indicador de carga fijo hasta que el
  // documento esta listo, para que el usuario no vea parpadear la barra de herramientas ni
  // el area gris mientras DevExpress construye/reconstruye el documento.
  const [documentReady, setDocumentReady] = useState(false);
  const [viewerFailed, setViewerFailed] = useState(false);

  // Reinicia el guard de reintento cada vez que el reporte a mostrar cambia (nuevo
  // resolvedId -- p.ej. este mismo ReportViewer resolvió otro report_id via pageKey).
  // Cuando el remount viene de afuera (NominaPolizaViewer/NominaResumenViewer cambian su
  // propio `key`), se crea una instancia nueva de ReportViewer con refs en su valor inicial,
  // así que no hace falta nada extra ahí.
  useEffect(() => {
    hasRenderedOnceRef.current = false;
    retryCountRef.current = 0;
    autoSubmitDoneRef.current = false;
    setDocumentReady(false);
    setViewerFailed(false);
    // Limpia cualquier reintento pendiente de la instancia anterior: si resolvedId cambia
    // (o el componente se desmonta) antes de que el setTimeout de abajo dispare, este cleanup
    // lo cancela -- sin esto, el reintento se perdía en silencio (nunca llegaba a pedirse al
    // servidor) cuando el usuario cambiaba de Nómina/tab justo despues de un fallo, dejando
    // el visor cubierto por el spinner hasta el timeout de 60s sin haber agotado sus intentos.
    return () => {
      if (retryTimeoutIdRef.current) {
        clearTimeout(retryTimeoutIdRef.current);
        retryTimeoutIdRef.current = null;
      }
    };
  }, [resolvedId]);

  // Condición de carrera confirmada (logs de reporteador-reporteador-1, 2026-10-19): al
  // remontar el DxReportViewer justo cuando termina un cálculo de Nómina (ver
  // refreshKeyPostCalculo en CalculosDeNominaEncabezado.jsx), el primer StartBuild del
  // visor recién creado sale vacío ("Id: ''. Name: ''.") y el reporteador lo rechaza con
  // ArgumentException "ReportId should be a valid identifier" -- el mismo reporte se
  // reconstruye bien un instante después si se reintenta. Aquí se detecta ese primer fallo
  // (antes de que el visor haya mostrado un documento exitosamente) y se reintenta remontando
  // solo el DxReportViewer interno (key), para no esconder un error real si el reintento
  // también falla repetidamente o si el fallo ocurre sobre un reporte que ya estaba visible.
  //
  // MAX_RETRIES > 1 (antes era un solo intento, vía un flag booleano en vez de contador):
  // confirmado en logs (Nomina 612, reporte Pólizas, 2026-11-02 22:56:29 UTC) que un único
  // intento no bastaba -- no quedó ningún segundo StartBuild registrado para ese reporte en
  // los 81s siguientes, es decir el (único) reintento se perdió sin llegar a pedirse al
  // servidor (ver cleanup de arriba).
  const MAX_RETRIES = 3;
  const RETRY_DELAY_MS = 1500;
  const handleServerError = () => {
    if (hasRenderedOnceRef.current || retryCountRef.current >= MAX_RETRIES) {
      return;
    }
    retryCountRef.current += 1;
    retryTimeoutIdRef.current = setTimeout(() => {
      retryTimeoutIdRef.current = null;
      if (hasRenderedOnceRef.current || autoSubmitDoneRef.current) return;
      setRetryNonce((n) => n + 1);
    }, RETRY_DELAY_MS);
  };

  // El visor DevExpress no respeta de forma confiable ShouldRequestParameters/Submit del
  // propio evento ParametersInitialized en esta version (24.2.15) para forzar el envio sin
  // interaccion -- model.submit() queda como no-op hasta que el binding real del boton lo
  // reemplaza. Como alternativa confirmada que SI funciona (idéntica a que el usuario haga
  // clic en "Enviar"), se localiza y clickea el boton real una vez que aparece en el DOM.
  // El primer click puede caer antes de que DevExtreme termine de enlazar el handler interno
  // del widget boton (el elemento ya existe en el DOM, pero su binding de click aun no) --
  // por eso se reintenta varias veces en vez de detenerse en el primer click exitoso; un
  // "Enviar" de mas es inofensivo (vuelve a pedir el mismo reporte con los mismos valores).
  useEffect(() => {
    if (!hideParametersPanel) return undefined;

    let attempts = 0;
    const maxAttempts = 20; // ~6s a 300ms por intento
    autoSubmitDoneRef.current = false;
    const intervalId = setInterval(() => {
      // Cada "Enviar" de mas vuelve a pedir el documento y reinicia el mensaje "Creando el
      // documento..." (parpadeo visible): se deja de clicar apenas el envio surte efecto
      // (ParametersSubmitted) o el documento ya esta listo (DocumentReady).
      if (autoSubmitDoneRef.current || hasRenderedOnceRef.current) {
        clearInterval(intervalId);
        return;
      }
      attempts += 1;
      const actionsContainer = document.querySelector('.dxrd-preview-parameter-actions-container');
      const buttons = actionsContainer?.querySelectorAll('.dx-button') || [];
      const submitButton = buttons[buttons.length - 1];
      if (submitButton) {
        submitButton.click();
      }
      if (attempts >= maxAttempts) {
        clearInterval(intervalId);
      }
    }, 300);

    return () => clearInterval(intervalId);
    // retryNonce: tras el remount automático de handleServerError, el DOM del panel de
    // parámetros se recrea y hay que volver a buscar/clickear el botón "Enviar" en él.
  }, [hideParametersPanel, resolvedId, retryNonce]);

  useEffect(() => {
    if (!hideParametersPanel) return undefined;
    const timeoutId = setTimeout(() => setViewerFailed(true), 60000);
    return () => clearTimeout(timeoutId);
  }, [hideParametersPanel, resolvedId, retryNonce]);

  useImperativeHandle(ref, () => ({
    getParameterValue: (name) => {
      const model = viewerRef.current?.instance()?.GetParametersModel();
      return model?.parameters?.find((p) => p.path === name)?.value;
    },
  }));

  // Si se proporcionó pageKey, obtener el report_id desde Django.
  useEffect(() => {
    if (reportIdProp) {
      return undefined;
    }
    if (!pageKey) {
      setError('Debe proporcionarse pageKey o reportId.');
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    getReportesConfig(pageKey)
      .then((data) => {
        if (cancelled) return;
        // El backend puede ignorar el filtro ?page_key= y devolver la lista completa,
        // por lo que se filtra aquí por seguridad (mismo resguardo que el Reporteador anterior).
        const reportes = Array.isArray(data) ? data : [];
        const reporte = reportes.find((r) => r.page_key === pageKey);
        if (!reporte) {
          setError(`No se encontró reporte configurado para la página "${pageKey}".`);
          return;
        }
        setResolvedId(reporte.report_id);
      })
      .catch(() => {
        if (!cancelled) setError('Error al obtener la configuración del reporte.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [pageKey, reportIdProp]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
        <Spin size="large" tip="Cargando reporte..." />
      </div>
    );
  }

  if (error) {
    return <Alert type="error" message={error} showIcon style={{ margin: 16 }} />;
  }

  // Los parametros tambien se mandan "horneados" en la propia reportUrl
  // ("external:<id>?nomina=573"): el reporteador (CustomReportProvider.GetReport /
  // CustomXPOReportStorageWebExtension.GetData) los fija en report.Parameters del lado
  // servidor, ANTES de construir el documento, con RequestParameters=false -- esta es la
  // via confiable. El Callbacks.ParametersInitialized de abajo (simular clic en "Enviar")
  // queda solo como respaldo si algun reporte no trajera ya el valor horneado.
  const parametrosQueryString = parameters && Object.keys(parameters).length
    ? new URLSearchParams(
      Object.entries(parameters).filter(([, value]) => value !== undefined && value !== null),
    ).toString()
    : '';
  const reportUrl = parametrosQueryString
    ? `external:${resolvedId}?${parametrosQueryString}`
    : `external:${resolvedId}`;

  // El componente oficial maneja templates, Knockout y el panel de parámetros
  // internamente (vía su propio TemplateEngine de React).
  const taparVisor = hideParametersPanel && !documentReady && !viewerFailed;

  return (
    <div
      ref={containerRef}
      className={hideParametersPanel ? 'dx-report-viewer-hide-parameters-panel' : undefined}
      style={{ height, position: 'relative' }}
    >
      {taparVisor && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            // DevExpress pinta su propia barra de progreso/error (.dxrd-preview-progress) con
            // z-index:10 -- igual al que tenia este overlay. Con el mismo valor, el elemento
            // mas reciente en el DOM gana el empate (el de DevExpress, anidado dentro de
            // DxReportViewer, que se renderiza despues de este div), dejando ver por encima el
            // aviso rojo "Internal Server Error" de la condicion de carrera ya mitigada en
            // handleServerError. Un z-index bien por encima de cualquier overlay interno del
            // visor (los popups de DevExpress llegan hasta 1000) evita ese empate.
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#fff',
          }}
        >
          <Spin size="large" tip="Cargando reporte..." />
        </div>
      )}
      <DxReportViewer
        // Cambiar la key fuerza a React a destruir y recrear este widget desde cero --
        // es el mismo efecto que cerrar y volver a abrir el tab, usado por
        // handleServerError para reintentar la condición de carrera documentada arriba.
        key={retryNonce}
        ref={viewerRef}
        reportUrl={reportUrl}
        height={height}
        developmentMode={process.env.NODE_ENV === 'development'}
      >
        <RequestOptions host={REPORTEADOR_HOST} invokeAction={INVOKE_ACTION} />
        <Callbacks
          DocumentReady={() => {
            hasRenderedOnceRef.current = true;
            autoSubmitDoneRef.current = true;
            setDocumentReady(true);
          }}
          ParametersSubmitted={() => { autoSubmitDoneRef.current = true; }}
          OnServerError={handleServerError}
          ParametersInitialized={(args) => {
            if (!parameters) return;
            const model = args.ParametersModel;
            if (!model) return;
            Object.entries(parameters).forEach(([name, value]) => {
              // setParameterValue() es el setter publico del modelo (ParametersPanelModelBase):
              // a diferencia de mutar `parameters[i].value` directo, dispara la logica interna
              // de validacion/lookups dependientes que el visor espera que corra al cambiar un
              // valor de parametro.
              model.setParameterValue?.(name, value);
            });
          }}
        />
      </DxReportViewer>
    </div>
  );
});

ReportViewer.propTypes = {
  pageKey: PropTypes.string,
  reportId: PropTypes.number,
  height: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  parameters: PropTypes.object,
  hideParametersPanel: PropTypes.bool,
};

ReportViewer.defaultProps = {
  pageKey: null,
  reportId: null,
  height: '80vh',
  parameters: null,
  hideParametersPanel: false,
};

export default ReportViewer;
